import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';

const CsvUpload = ({ leagueId, onSuccess, onClose, open }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      setSelectedFile(file);
      setResult(null);
    } else {
      alert('Please select a CSV file');
      event.target.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const uploadResult = await unifiedApi.uploadGamesCsv(leagueId, selectedFile);
      setResult(uploadResult);

      if (uploadResult?.success) {
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setResult({
        success: false,
        errors: ['Upload failed: ' + error.message]
      });
    }
    setUploading(false);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    setUploading(false);
    onClose?.();
  };

  const buildSuccessSummary = (uploadResult) => {
    const parts = [];
    const createdGames = uploadResult?.createdGamesCount || 0;
    const createdPlaceholders = uploadResult?.createdPlaceholdersCount || 0;
    const updatedPlaceholders = uploadResult?.updatedPlaceholdersCount || 0;
    const replacedPlaceholders = uploadResult?.replacedPlaceholdersCount || 0;

    if (createdGames > 0) parts.push(`${createdGames} game${createdGames === 1 ? '' : 's'} created`);
    if (createdPlaceholders > 0) parts.push(`${createdPlaceholders} placeholder${createdPlaceholders === 1 ? '' : 's'} created`);
    if (updatedPlaceholders > 0) parts.push(`${updatedPlaceholders} placeholder${updatedPlaceholders === 1 ? '' : 's'} updated`);
    if (replacedPlaceholders > 0) parts.push(`${replacedPlaceholders} placeholder${replacedPlaceholders === 1 ? '' : 's'} replaced`);

    return parts.length > 0 ? `Upload complete: ${parts.join(', ')}.` : 'Upload complete.';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Schedule CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Upload a CSV file to create multiple games at once. The CSV should have the following columns:
          </Typography>
          
          <Typography variant="body2" component="pre" sx={{ 
            backgroundColor: 'grey.100', 
            p: 1, 
            borderRadius: 1,
            fontSize: '0.75rem',
            mb: 2,
            overflowX: 'auto'
          }}>
{`Date,Start Time,Duration,Venue,Field,Home Team,Away Team,Home Score,Away Score,Game Type
Thu May 22,8:30 PM,60,Julian de Guzman Park,Field 1,Pink Pony FC,Dani Rojos FC,2,7,Regular
Thu May 29,9:30 PM,60,Julian de Guzman Park,Field 2,Team A,Team B,,,Playoff
Fri May 30,8:00 PM,60,Julian de Guzman Park,Field 1,Team C,Team D,,,SemiFinal
Fri May 30,9:00 PM,60,Julian de Guzman Park,Field 1,Team A,Team C,,,Final
Sat May 31,8:00 PM,60,Julian de Guzman Park,,Team E,Team F,,,Friendly`}
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            • Date: Any date format (e.g., "Thu May 22", "2025-05-22")
            <br />
            • Start Time: Time format (e.g., "8:30 PM", "20:30")
            <br />
            • Duration: Duration in minutes (e.g., "60", "90")
            <br />
            • Venue/Field: Venue must match an existing venue; Field can be blank for Friendly games
            <br />
            • Teams: Must match existing team names in this league
            <br />
            • Scores: Leave blank for unplayed games, or enter numbers
            <br />
            • Game Type (optional): Regular, Playoff, Friendly, SemiFinal, or Final (defaults to Regular)
          </Typography>

          <Box sx={{ my: 3 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Choose CSV File
              </Button>
            </label>
            
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>

          {result && (
            <Box sx={{ mt: 2 }}>
              {result.success ? (
                <Alert severity="success">
                  {buildSuccessSummary(result)}
                  {result.warnings?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Warnings:</Typography>
                      <List dense>
                        {result.warnings.map((warning, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={warning} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Alert>
              ) : (
                <Alert severity="error">
                  Upload failed
                  {result.errors?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Errors:</Typography>
                      <List dense>
                        {result.errors.map((error, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          variant="contained"
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CsvUpload;